import placeTypesConfig from "../../../priv/static/place_types_config.json"
import { fetchPlacePredictions } from "./place_selector_api.mjs"

const TYPES_EXCLUDED = placeTypesConfig.excluded_types

let predictionFilter = (prediction) =>
  prediction.types.filter((type) => TYPES_EXCLUDED.includes(type)).length === 0

let predictionMapper = ({ placeId, text }) => {
  // FormattableText.toString() is unreliable: depending on how the Maps JS
  // library was loaded (e.g. places-only via the js-api-loader bootstrap) it
  // returns undefined even on current versions, blanking the dropdown. Read the
  // documented `.text` property first, fall back to toString() just in case.
  const fullText = text?.text || text?.toString?.() || ""

  return {
    data: { place_id: placeId, name: fullText },
    optionText: fullText,
    selectionText: fullText,
    imageUrl: null,
  }
}

let filterPredictions = (predictions) =>
  predictions.filter(predictionFilter).map(predictionMapper)

export const placeSelector = {
  mounted() {
    // ensure input is disabled when mounting
    disablePlaceSelector()

    const extraParams = JSON.parse(this.el.dataset.extraParams || "{}")

    this.el.addEventListener("autocomplete:item-select", (e) => {
      this.pushEventTo(
        this.el,
        this.el.dataset.onSelect,
        Object.assign({}, extraParams, e.detail.data),
      )
    })

    this.el.addEventListener("autocomplete:leaving", (e) => {
      // Merge extraParams so the payload carries `_target_module` (ENA-9491):
      // pushEventTo serializes only this object, never the surrounding form, so the
      // form's hidden `_target_module` input never reaches the server on this event.
      // Without it a misrouted blur cannot match the ENA-9204 guard and is swallowed
      // by the injected no-op instead of being reported.
      this.pushEventTo(
        this.el,
        "place-selector:blur-without-selection",
        Object.assign({}, extraParams, { address: e.target.value }),
      )
    })

    this.el.addEventListener("input", (e) => {
      if (e.target.value == "") {
        const elementId = e.target.getAttribute("id")
        const id = elementId.replace("_display", "")
        document.getElementById(id).value = ""
        this.pushEventTo(this.el, this.el.dataset.onClear, extraParams)
      }
    })

    this.el.addEventListener("paste", (e) => {
      if (this.el.dataset.selectOnPaste == "") {
        const address = e.clipboardData.getData("text/plain")
        this.pushEventTo(
          this.el,
          "place-selector:paste",
          Object.assign({}, extraParams, { address }),
        )
      }
    })

    initAutocomplete()
  },
}

function initAutocomplete() {
  const autocompleteSessionToken = globalThis.crypto?.randomUUID?.()

  window.fetchPlacesForAutocomplete ||= async (query) => {
    try {
      const predictions = await fetchPlacePredictions(query, {
        apiKey: ENAIA_GOOGLE_MAPS_FRONTEND_KEY,
        includedPrimaryTypes: placeTypesConfig.included_primary_types,
        sessionToken: autocompleteSessionToken,
      })

      return filterPredictions(predictions)
    } catch (error) {
      console.error("[PlaceSelector] Error fetching suggestions:", error)
      return []
    }
  }

  // Unfiltered variant for admin tools that need to find any place type
  window.fetchAllPlacesForAutocomplete ||= async (query) => {
    try {
      const predictions = await fetchPlacePredictions(query, {
        apiKey: ENAIA_GOOGLE_MAPS_FRONTEND_KEY,
        sessionToken: autocompleteSessionToken,
      })

      return predictions.map(predictionMapper)
    } catch (error) {
      console.error("[PlaceSelector] Error fetching suggestions:", error)
      return []
    }
  }

  enablePlaceSelector()
}

const selectorQuery = '[data-autocomplete-init="true"]'
function disablePlaceSelector() {
  document.querySelectorAll(selectorQuery).forEach((input) => {
    input.setAttribute("disabled", true)
  })
}
function enablePlaceSelector() {
  document.querySelectorAll(selectorQuery).forEach((input) => {
    input.removeAttribute("disabled")
  })
}
